import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

/* Minimal runtime for the authored hero compositions: one looping clock,
   a scaled fixed-size stage, and the easing/animate helpers they are written against. */

export const clamp = (v, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
export const lerp = (a, b, t) => a + (b - a) * t;

export const Easing = {
  linear: (t) => t,
  easeOutQuad: (t) => 1 - (1 - t) * (1 - t),
  easeOut: (t) => 1 - Math.pow(1 - t, 3),
  easeOutCubic: (t) => 1 - Math.pow(1 - t, 3),
  easeInOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  easeInOutSine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
  easeOutBack: (t) => 1 + 2.7015 * Math.pow(t - 1, 3) + 1.7015 * Math.pow(t - 1, 2),
};

export const animate = ({ from = 0, to = 1, start = 0, end = 1, ease = Easing.easeInOutCubic }) => (T) => {
  if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(T)) return from;
  const raw = clamp((T - start) / Math.max(0.0001, end - start), 0, 1);
  return lerp(from, to, ease(raw));
};

export const interpolate = (T, [s, e], [from, to], ease = Easing.linear) => animate({ from, to, start: s, end: e, ease })(T);

const Ctx = createContext({ T: 0, CUES: {}, authoredTotal: 1 });
export const useComposition = () => useContext(Ctx);

/* scenes: [{ name, dur }] — cue times are the running sum */
export const cuesFrom = (scenes) => {
  let at = 0;
  const CUES = {};
  scenes.forEach((s) => { CUES[s.name] = at; at += s.dur; });
  return { CUES, total: at };
};

export function CompositionStage({ width, height, scenes, children, className = "", style, minScale = 0, maxHeight }) {
  const { CUES, total } = cuesFrom(scenes);
  const [T, setT] = useState(0);
  const [scale, setScale] = useState(1);
  const box = useRef(null);

  useLayoutEffect(() => {
    const measure = () => {
      const el = box.current;
      if (el && el.clientWidth > 0) setScale(Math.max(el.clientWidth / width, minScale));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [width, minScale]);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setT(total * 0.78);
      return;
    }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      setT(((now - t0) / 1000) % total);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  return (
    <div ref={box} className={className} style={{ position: "relative", width: "100%", height: Math.min(height * scale, maxHeight || Infinity), overflow: "hidden", ...style }}>
      <div style={{ position: "absolute", left: 0, top: 0, width, height, transform: `scale(${scale})`, transformOrigin: "0 0" }}>
        <Ctx.Provider value={{ T, CUES, authoredTotal: total, compact: scale < 0.6 }}>{children}</Ctx.Provider>
      </div>
    </div>
  );
}
